<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-10">
        <div class="flex flex-col w-full">
          <form-card
            v-if="(isConfirmationMode || isViewMode)"
            :title="t('pages.visibility.planning.modal.chooseBicycles')"
            :description="t('pages.visibility.planning.modal.chooseBicyclesDescription')"
          >
            <div class="flex flex-col gap-y-5">
              <BicyclePickLocationMap
                :read-only="isViewMode"
                :visibility-location="visibilityLocationOption"
                :visibility-bicycles="visibilityBicyclesForMap"
                :radius="radius"
                @update:visibilityBicycles="updateFreeVisibilityBicycles"
              />
              <MultiselectTags
                v-model="rawVisibilityPlanning.visibilityBicycles"
                :options="visibilityBicyclesFree"
                :error="errors?.visibilityBicycles"
                :label="t('pages.visibility.planning.modal.visibilityBicycles')"
                :can-deselect="true"
                :can-clear="false"
                :disabled="loadingBicycles || isViewMode"
                :loading="loadingBicycles"
                mode="tags"
                class="mt-3 mb-5"
              />
              <Multiselect
                v-model="rawVisibilityPlanning.responsibleUserForTransfer"
                :error="errors?.responsibleUserForTransfer"
                :options="userList"
                :label="t('visibility.planning.responsibleUserForTransfer')"
                :loading="loadingUsers"
                :disabled="loadingUsers"
                class="w-full"
                :can-deselect="false"
                :can-clear="true"
                :searchable="true"
              />
            </div>
          </form-card>
          <form-card
            :title="t(tKey + '.location.title')"
            :description="t(tKey + '.location.description')"
          >
            <div class="flex flex-col gap-y-5">
              <Multiselect
                v-model="rawVisibilityPlanning.region"
                :disabled="isConfirmationMode || isViewMode"
                :error="errors?.region"
                :options="regions"
                :loading="loadingRegions"
                :label="t('visibility.planning.region')"
                class="w-full"
                :can-deselect="false"
                :can-clear="true"
                :searchable="true"
                @select="setMapCenterAndUpdate()"
              />
              <Multiselect
                v-model="rawVisibilityPlanning.campaign"
                :error="errors?.campaign"
                :options="campaigns"
                :label="t('visibility.planning.campaign')"
                :loading="loadingCampaigns"
                :disabled="!rawVisibilityPlanning.region || loadingCampaigns"
                class="w-full"
                :can-deselect="false"
                :can-clear="true"
                :searchable="true"
                @select="updateCampaignTimes()"
                @update="updateCampaignTimes()"
              />
              <Multiselect
                v-model="rawVisibilityPlanning.location"
                :error="errors?.location"
                :options="visibilityLocations"
                :label="t('visibility.newLocation')"
                :disabled="!rawVisibilityPlanning.region || loadingLocations || isConfirmationMode || isViewMode"
                :loading="loadingLocations"
                class="w-full"
                :can-deselect="false"
                :can-clear="true"
                :searchable="true"
                @select="setMapCenterAndUpdate()"
                @update="setMapCenterAndUpdate()"
              />
              <LocationMap
                ref="map"
                v-model="mapLocation"
                v-model:radius="mapRadius"
              />
            </div>
          </form-card>
          <form-card
            :title="t('general')"
            :description="t(tKey + '.general.description')"
          >
            <div class="flex flex-col gap-y-5">
              <Multiselect
                v-if="!isViewMode && !isConfirmationMode"
                v-model="selectedWeek"
                :options="availableWeeksToSelect"
                :label="t('pages.visibility.planning.modal.new.selectedWeek')"
                :disabled="!rawVisibilityPlanning.region || loadingLocations || isConfirmationMode || isViewMode"
                :loading="loadingCampaigns"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <date-range
                v-model="rawVisibilityPlanning.time"
                :errors="errors?.time"
                :disabled="isConfirmationMode || isViewMode"
                :label="t('pages.visibility.planning.modal.new.dateRange')"
                :placeholder="t('pages.visibility.planning.modal.new.dateRange')"
                mode="datetime"
                variant="narrow"
              />
              <FormInput
                v-model="rawVisibilityPlanning.bicycleCount"
                :error="errors?.bicycleCount"
                :disabled="isConfirmationMode || isViewMode || isEditMode"
                type="number"
                :label="t('visibility.planning.bicycleCount')"
                :placeholder="t('visibility.planning.bicycleCount')"
              />
              <div v-if="!isViewMode && !isConfirmationMode" class="font-bold text-red-120 mb-8">
                <div v-if="!loadingVisibilityPlanningCapacityDto">
                  <p>{{ capacityText }}</p>
                  <p>{{ usageText }}</p>
                  <p>{{ quotaText }}</p>
                </div>
                <div v-else class="flex justify-left">
                  <Spinner class="w-1 h-1" />
                </div>
              </div>
              <FormInput
                v-model="rawVisibilityPlanning.newDesign"
                :error="errors?.newDesign"
                type="text"
                :label="t('visibility.newDesign')"
                :placeholder="t('visibility.newDesign')"
              />
              <FormInput
                v-model="rawVisibilityPlanning.comment"
                :error="errors?.comment"
                type="text"
                :label="t('visibility.comment')"
                :placeholder="t('visibility.comment')"
              />
              <Switch
                v-if="!isConfirmationMode && !isViewMode"
                v-model="chaining"
                :label="t('pages.visibility.planning.modal.new.chaining.label')"
                :options="{
                  yes: t('yes'),
                  no: t('no'),
                }"
              />
              <p v-if="!isConfirmationMode && !isViewMode && chainingCtr > 0">
                {{ chainingCtr + " " + t('pages.visibility.planning.modal.new.chainCtrText') }}
              </p>
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, nextTick, onMounted, reactive, type Ref, ref, watch, watchEffect,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidationExt } from '@hooks/form';
import {
  array, date, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import {
  type Filter,
  type ProviderParams, useProvider, useProviderOpt,
} from '@utils/table/hooks/provider';
import Switch from '@utils/inputs/Switch.vue';
import { useQueryRef } from '@hooks/url';
import Spinner from '@utils/Spinner.vue';
import MultiselectTags from '@utils/MultiselectTags.vue';
import BicyclePickLocationMap from '@utils/maps/BicyclePickLocationMap.vue';
import DateRange from '@utils/inputs/DateRange.vue';
import { addDays, differenceInDays } from 'date-fns';
import { useConfirmPrompt } from '@utils/prompt/injects';
import {
  type Campaign,
  type CampaignOpt,
  type EnrichedVisibilityBicycleDto,
  type EnrichedVisibilityBicycleOption,
  type GisLocation, type Region,
  type RegionOption, type User,
  type VisibilityBicycle,
  type VisibilityBicycleDetail,
  type VisibilityLocation,
  type VisibilityLocationOption, VisibilityLocationStatus,
  type VisibilityPlanning,
  type VisibilityPlanningCapacityDto,
  type VisibilityPlanningForm,
  type VisibilityPlanningFormUnion,
  VisibilityPlanningModalMode,
  VisibilityPlanningStatus,
} from '@/entities/Entities';

interface Props {
  modalMode?: VisibilityPlanningModalMode;
  open: boolean;
  visibilityPlanning?: VisibilityPlanning;
}

const chainingCtr = ref(0);
const radius = ref(100);
const emit = defineEmits<{(e: 'update:open', value: boolean): void;
  (e: 'update:visibilityPlanning', value?: VisibilityPlanning): void;
  (e: 'saved'): void;
  (e: 'update:visibilityBicycles', value: EnrichedVisibilityBicycleOption[]): void;
}>();
const props = defineProps<Props>();
const modalMode = computed(() => props.modalMode);
const isEditMode = computed(() => modalMode.value === VisibilityPlanningModalMode.EDIT);
const isConfirmationMode = computed(() => modalMode.value === VisibilityPlanningModalMode.CONFIRM);
const isViewMode = computed(() => modalMode.value === VisibilityPlanningModalMode.VIEW);
const internalOpen = useVModel(props, 'open', emit);
const internalVisibilityPlanning = useVModel(props, 'visibilityPlanning', emit);
const isNew = computed(() => !props.visibilityPlanning);
const tKey = computed(() => (isNew.value ? 'pages.visibility.planning.modal.new' : 'pages.visibility.planning.modal.edit'));

const { t } = useI18n();
const notify = useNotify();
const confirm = useConfirmPrompt();
const chaining = ref(false);

const availableWeeksToSelect = computed(() => {
  // eslint-disable-next-line no-use-before-define
  if (!rawVisibilityPlanning.value.campaign || !campaigns.value) {
    return [];
  }
  // eslint-disable-next-line no-use-before-define
  const campaign = campaigns.value.find((c) => c.value === rawVisibilityPlanning.value.campaign);
  if (!campaign) {
    return [];
  }

  const startTime = new Date(campaign.actualStartDate);
  const endTime = new Date(campaign.actualEndDate);

  if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) {
    return [];
  }

  const totalDays = differenceInDays(endTime, startTime);
  const totalWeeks = Math.ceil(totalDays / 7);

  return [
    { value: 0, label: t('pages.visibility.planning.modal.new.allCampaignWeeks') }, // Always include week 0
    ...Array.from({ length: totalWeeks }, (_, index) => ({
      value: index + 1,
      label: `${t('pages.visibility.planning.modal.new.campaignWeek')} ${index + 1}`,
    })),
  ];
});

const initialData = (visibilityPlanning?: VisibilityPlanning): VisibilityPlanningFormUnion => (visibilityPlanning
  ? {
    ...visibilityPlanning,
    time: {
      start: visibilityPlanning.startTime ?? null,
      end: visibilityPlanning.endTime ?? null,
    },
    region: visibilityPlanning?.region?.['@id'],
    campaign: visibilityPlanning?.campaign?.['@id'],
    location: visibilityPlanning?.location?.['@id'],
    responsibleUserForTransfer: visibilityPlanning?.responsibleUserForTransfer?.['@id'],
    visibilityBicycles: visibilityPlanning?.visibilityBicycles?.map((b: VisibilityBicycle | EnrichedVisibilityBicycleOption) => {
      // Find the corresponding enriched object for each IRI
      // eslint-disable-next-line no-use-before-define
      const bicycles = isViewMode.value ? visibilityBicyclesAll.value : visibilityBicyclesFree.value;
      const mapped = bicycles.find((bicycle) => bicycle.value === b['@id']);

      const visibilityBicycle = b as VisibilityBicycle;

      if (!mapped) {
        console.error('wrong data received');
        return visibilityBicycle['@id'];
      }

      // Update mapped object
      return {
        ...mapped,
        value: visibilityBicycle['@id'],
        label: `${visibilityBicycle.region?.name} ${visibilityBicycle?.number}`,
      };
    }) || [],
  }
  : {
    campaign: undefined,
    region: undefined,
    responsibleUserForTransfer: undefined,
    location: undefined, // Updated `newLocation` -> `location`
    visibilityBicycles: [], // Ensure it's an array, not undefined
    bicycleCount: 0,
    bicycleCountPlanned: 0, // New property
    startTime: undefined,
    endTime: undefined,
    status: VisibilityPlanningStatus.DRAFT,
    newDesign: '',
    comment: '',
    createdAt: undefined,
    updatedAt: undefined,
    time: {
      start: null,
      end: null,
    },
  });

const rawVisibilityPlanning = syncRef(
  internalVisibilityPlanning,
  (visibilityPlanning: VisibilityPlanning | undefined) => ({ ...initialData(visibilityPlanning) }),
);

const updateFreeVisibilityBicycles = (newBicycles: EnrichedVisibilityBicycleOption[]) => {
  rawVisibilityPlanning.value.visibilityBicycles = newBicycles.filter((b) => b.selected);
  // Ensure that the map updates with the correct selection
  emit('update:visibilityBicycles', [...newBicycles]);
};

const {
  data: userList,
  loading: loadingUsers,
} = useProviderOpt<User>(
  '/users',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    filter: {
      noDrivers: 'true',
      roles: 'ROLE_VISIBILITY',
    },
  },
  (value: User) => ({
    label: value.fullName,
    value: value['@id'],
  }),
  {
    immediate: true,
  },
);

const {
  data: regions,
  loading: loadingRegions,
  refetch: loadRegionsRaw,
} = useProviderOpt<RegionOption>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value: Region) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
  { immediate: false },
);

const filterBicycles = computed(() => {
  if (isViewMode.value) {
    return {} as Filter;
  }

  return {
    region: rawVisibilityPlanning.value.region ?? '',
    availableFrom: rawVisibilityPlanning.value.startTime ?? '',
    availableTo: rawVisibilityPlanning.value.endTime ?? '',
  } as Filter;
});

const paramsBicycles: ProviderParams = reactive({
  filter: {} as Filter,
  sort: {
    sortKey: 'visibilityBicycle.number',
    sortDirection: 'ASC',
  },
});

watchEffect(() => {
  paramsBicycles.filter = filterBicycles.value;
});

function isSameVisibilityBicycleOption(vb: EnrichedVisibilityBicycleOption, value: any): boolean {
  return vb.value === value.value
    && vb.label === value.label
    && vb.visibilityBicycle.region === value.region;
}

function isEnrichedVisibilityBicycleOption(vb: any): vb is EnrichedVisibilityBicycleOption {
  return vb && typeof vb === 'object' && 'value' in vb;
}

const transformDtoToOption = (value: EnrichedVisibilityBicycleDto) =>
  ({
    label: `${value.visibilityBicycle?.region?.name} ${value.visibilityBicycle?.number}`,
    value: value.visibilityBicycle?.['@id'],
    gisLocation: {
      lat: value.currentLocationForBicycle?.gisLocation?.lat ?? value.visibilityTracker?.latestPoints?.[0]?.location?.lat ?? 40,
      long: value.currentLocationForBicycle?.gisLocation?.long ?? value.visibilityTracker?.latestPoints?.[0]?.location?.lng ?? 40,
    },
    visibilityBicycle: value.visibilityBicycle,
    visibilityTracker: value.visibilityTracker,
    selected: !!rawVisibilityPlanning.value.visibilityBicycles
      ?.find((vb) => isEnrichedVisibilityBicycleOption(vb) && isSameVisibilityBicycleOption(vb, value)),
  });

const {
  data: visibilityBicyclesFree,
  loading: loadingBicycles,
  refetch: loadBicyclesRaw,
} = useProviderOpt('/visibility_bicycles/tracker/free', paramsBicycles, transformDtoToOption, { immediate: false });

const loadBicycles = async () => {
  await loadBicyclesRaw();
  // Initialize or update rawVisibilityPlanning after bicycles are loaded
  // Ensure selected bicycles are properly mapped to enriched data
  if (internalVisibilityPlanning.value && !isViewMode.value) {
    rawVisibilityPlanning.value.visibilityBicycles = internalVisibilityPlanning.value.visibilityBicycles?.map((bicycle) => {
      const matchedBicycle = visibilityBicyclesFree.value.find((v) => v.value === bicycle['@id']);
      return matchedBicycle || bicycle['@id'];
    }) || [];
  }
};

const { data: visibilityBicyclesAll } = useProviderOpt('/visibility_bicycles/tracker', paramsBicycles, transformDtoToOption);

const filterLocations = computed(() => ({
  region: rawVisibilityPlanning.value.region ?? '',
  status: VisibilityLocationStatus.ACTIVE,
}));

const paramsLocations: ProviderParams = reactive({
  sort: {
    sortKey: 'name',
    sortDirection: 'ASC',
  },
  filter: isViewMode.value ? {} : filterLocations,
});

const {
  data: visibilityLocations,
  loading: loadingLocations,
  refetch: loadLocationsRaw,
} = useProviderOpt<VisibilityLocationOption>(
  '/visibility_locations',
  paramsLocations,
  (value: VisibilityLocation) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
    radius: value.radius,
    quota: value.quota,
  }),
  {
    immediate: false,
  },
);
const loadLocations = async () => {
  await loadLocationsRaw();
  // eslint-disable-next-line no-use-before-define
  setMapCenterAndUpdate();
};

const visibilityLocationOption = computed(() => visibilityLocations
  ?.value
  .find((l) => (l as VisibilityLocationOption).value === rawVisibilityPlanning.value.location) as VisibilityLocationOption ?? null);

const sortKey = useQueryRef<string | undefined>('sortKey', 'startDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');
const filterCampaigns = computed(() => ({
  region: rawVisibilityPlanning.value.region ?? '',
  'actualEndDate[after]': new Date().toISOString(),
  name: '',
}));

const paramsCampaign: ProviderParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: filterCampaigns,
});

const {
  data: campaigns,
  loading: loadingCampaigns,
  refetch: loadCampaigns,
} = useProviderOpt<Campaign, CampaignOpt>(
  '/campaigns',
  paramsCampaign,
  (value: Campaign) => ({
    label: value.name,
    value: value['@id'],
    actualStartDate: value.actualStartDate,
    actualEndDate: value.actualEndDate,
  }),
);

watch(
  () => rawVisibilityPlanning.value.region,
  () => {
    loadCampaigns();
    loadLocations();
    if (!isViewMode.value && internalOpen) {
      loadBicycles();
    }
  },
  { immediate: false },
);

const visibilityBicyclesForMap = computed<VisibilityBicycleDetail[]>(() => {
  if (isViewMode.value) {
    return visibilityBicyclesAll.value.filter((b) =>
      rawVisibilityPlanning.value.visibilityBicycles.some((vb) => (vb as EnrichedVisibilityBicycleOption).value === b.value));
  }
  return visibilityBicyclesFree.value.map((b) => ({
    ...b,
    selected: rawVisibilityPlanning.value.visibilityBicycles.some((vb) => (vb as EnrichedVisibilityBicycleOption).value === b.value),
  }));
});

const validationSchema = computed(() => {
  if (isViewMode.value) {
    return object({
      campaign: string()
        .nullable()
        .required(t('validation.notEmpty')),
      comment: string()
        .nullable(),
    });
  }

  if (isConfirmationMode.value) {
    return object({
      visibilityBicycles: array()
        .of(object({
          value: string()
            .required(t('visibility.validation.valueMustBeString')),
        })
          .required())
        .min(1, t('visibility.validation.atLeastOneBicycle'))
        .test(
          'enough-bicycles',
          t('visibility.validation.matchBicycleCount'),
          (arr) => {
            const count = arr?.length;
            if (count === undefined) {
              return false;
            }
            const available = rawVisibilityPlanning.value.bicycleCount;
            if (!available) {
              return false;
            }

            return count === available;
          },
        ),
      campaign: string()
        .nullable()
        .required(t('validation.notEmpty')),
      newDesign: string()
        .nullable()
        .required(t('validation.notEmpty')),
      comment: string()
        .nullable(),
    });
  }

  return object({
    region: string()
      .nullable()
      .required(t('validation.notEmpty')),
    campaign: string()
      .nullable()
      .required(t('validation.notEmpty')),
    location: string()
      .nullable()
      .required(t('validation.notEmpty')),
    bicycleCount: number()
      .required(t('validation.notEmpty'))
      .integer()
      .min(1, t('validation.notEmpty'))
      .test(
        'enough-bicycles',
        t('visibility.validation.notEnoughBicycles'),
        (count) => {
          if (count === undefined) {
            return false;
          }
          // eslint-disable-next-line no-use-before-define
          const available = visibilityPlanningCapacityDto?.value?.availableBicycles;
          if (!available) {
            return false;
          }

          return count <= available;
        },
      ),
    time: object({
      start: date()
        .nullable()
        .required(t('validation.notEmpty'))
        .test(
          'start-after-campaign',
          t('visibility.validation.startAfterCampaign'),
          (start) => {
            if (!start) return false;
            const campaign = campaigns.value.find((c) => c.value === rawVisibilityPlanning.value.campaign);
            if (!campaign || !campaign.actualStartDate) return true;
            return new Date(start) >= new Date(campaign.actualStartDate);
          },
        ),
      end: date()
        .nullable()
        .required(t('validation.notEmpty'))
        .test(
          'end-after-start',
          t('visibility.validation.endAfterStart'),
          (end, { parent }) => {
            if (!end) return false;
            return parent.start ? new Date(end) > new Date(parent.start) : false;
          },
        ),
    })
      .required(t('validation.notEmpty')),
    status: string()
      .nullable()
      .required(t('validation.notEmpty')),
    newDesign: string()
      .nullable()
      .required(t('validation.notEmpty')),
    comment: string()
      .nullable(),
  });
});

const {
  data: visibilityPlanning,
  errors,
  validatedFn,
  setValidation,
} = useValidationExt<VisibilityPlanningForm>(validationSchema, (rawVisibilityPlanning as Ref<VisibilityPlanningForm>));

function resetData() {
  rawVisibilityPlanning.value = { ...initialData() } as VisibilityPlanningForm as typeof visibilityPlanning['value'];
  setValidation(false);
  chaining.value = false;
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalVisibilityPlanning.value = undefined;
}

const map = ref();

const mapLocation = ref<GisLocation>({
  lat: 0,
  long: 0,
});
const mapRadius = ref(2);

const setGisLocation = (gisLocation: GisLocation | undefined, locationRadius: number) => {
  if (!gisLocation) return;

  nextTick(() => {
    map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
    mapLocation.value.lat = gisLocation?.lat ?? 0;
    mapLocation.value.long = gisLocation?.long ?? 0;
    mapRadius.value = locationRadius;
  });
};

const setMapCenterAndUpdate = () => {
  if (rawVisibilityPlanning.value.location && visibilityLocations) {
    const location = visibilityLocations.value
      .find((vl) => (vl as VisibilityLocationOption).value === rawVisibilityPlanning.value.location) as VisibilityLocationOption;
    setGisLocation(
      location?.gisLocation,
      location?.radius,
    );
  } else if (rawVisibilityPlanning.value.region && regions) {
    const typedRegions = regions.value as RegionOption[];
    const location = typedRegions.find((r) => r.value === rawVisibilityPlanning.value.region);
    setGisLocation(location?.gisLocation, 2);
  }
};

onMounted(async () => {
  await loadRegionsRaw();
  setMapCenterAndUpdate();
});

const selectedWeekInternal = ref(0);

// compute the selectedWeek if its readonly mode
const selectedWeek = computed({
  get() {
    if (isConfirmationMode.value || isViewMode.value) {
      const campaign = campaigns.value.find((c) => c.value === rawVisibilityPlanning.value.campaign);
      const start = rawVisibilityPlanning.value.time?.start;
      if (!campaign || !start) return 0;

      return Math.floor(differenceInDays(new Date(start), new Date(campaign.actualStartDate)) / 7) + 1;
    }

    return selectedWeekInternal.value;
  },
  set(val) {
    selectedWeekInternal.value = val;
  },
});

watch(
  () => selectedWeek.value,
  () => {
    if (selectedWeek.value < 0) {
      return;
    }

    const campaign = campaigns.value.find((c) => c.value === rawVisibilityPlanning.value.campaign);
    if (!campaign) {
      return;
    }

    if (selectedWeek.value === 0 && rawVisibilityPlanning.value.time && !isViewMode.value && !isConfirmationMode.value) {
      rawVisibilityPlanning.value.time.start = new Date(campaign.actualStartDate);
      rawVisibilityPlanning.value.time.end = new Date(campaign.actualEndDate);
      return;
    }

    if (rawVisibilityPlanning.value.time) {
      const startTime = addDays(new Date(campaign.actualStartDate), 7 * (selectedWeek.value - 1));
      let endTime = addDays(new Date(campaign.actualStartDate), 7 * selectedWeek.value);

      // Ensure endTime does not exceed actualEndDate
      const campaignEndTime = new Date(campaign.actualEndDate);
      if (endTime > campaignEndTime) {
        endTime = campaignEndTime;
      }

      if (!isViewMode.value && !isConfirmationMode.value) {
        rawVisibilityPlanning.value.time.start = startTime;
        rawVisibilityPlanning.value.time.end = endTime;
      }
    }
  },
  { immediate: false },
);

const updateCampaignTimes = () => {
  if (rawVisibilityPlanning.value.campaign && !isViewMode.value && !isConfirmationMode.value) {
    const selectedCampaign = campaigns.value.find((c) => c.value === rawVisibilityPlanning.value.campaign);
    rawVisibilityPlanning.value.time.start = selectedCampaign?.actualStartDate ?? null;
    rawVisibilityPlanning.value.time.end = selectedCampaign?.actualEndDate ?? null;
    selectedWeek.value = 0;
    if (!rawVisibilityPlanning.value.newDesign) {
      rawVisibilityPlanning.value.newDesign = selectedCampaign?.label;
    }
  }
};

// Computed property for startTime in ISO format
const startTimeISO = computed(() => {
  const start = rawVisibilityPlanning.value?.time?.start;
  if (start) {
    const dateStart = new Date(start);
    if (!Number.isNaN(dateStart.getTime())) {
      return dateStart.toISOString();
    }
  }
  return '';
});

const endTimeISO = computed(() => {
  const end = rawVisibilityPlanning.value?.time?.end;
  if (end) {
    const newDate = new Date(end);
    if (!Number.isNaN(newDate.getTime())) {
      return newDate.toISOString();
    }
  }
  return '';
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  region: rawVisibilityPlanning.value.region ?? '',
  startTime: startTimeISO.value,
  endTime: endTimeISO.value,
  location: rawVisibilityPlanning.value.location ?? '',
}));

const totalFilter = computed(() => ({
  ...fixedFilter.value,
  ...filter.value,
}));

const providerParams = reactive({
  filter: totalFilter,
});

const {
  data: visibilityPlanningCapacityDto,
  loading: loadingVisibilityPlanningCapacityDto,
  refetch: refetchVisibilityPlanningCapacityDto,
} = useProvider<VisibilityPlanningCapacityDto>('/visibility_plannings/capacity', providerParams);

const capacityText = computed(() => {
  if (!visibilityPlanningCapacityDto.value) return '';
  // show number not below 0
  const textAvailable = Math.max(visibilityPlanningCapacityDto.value.availableBicycles ?? 0, 0);
  return `${textAvailable} / ${visibilityPlanningCapacityDto.value.totalBicycles} ${t('pages.visibility.planning.modal.capacityAreAvailable')}`;
});

const usageText = computed(() => {
  if (Number.isNaN(visibilityPlanningCapacityDto.value?.usedBicyclesOnLocation)) return '';
  return `${visibilityPlanningCapacityDto.value?.usedBicyclesOnLocation} ${t('pages.visibility.planning.modal.usedOnLocation')}`;
});

const quotaText = computed(() => {
  if (!visibilityLocationOption.value?.quota) return '';
  return `${visibilityLocationOption.value.quota} ${t('pages.visibility.planning.modal.recommendedOnLocation')}`;
});

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.visibilityPlanning;
    const url = edit ? props.visibilityPlanning['@id'] : '/visibility_plannings';

    if (!visibilityPlanning.value || loading.value) {
      console.error('invalid request to save');
      return;
    }

    if (isNew.value &&
      visibilityPlanning.value.bicycleCount !== null &&
      visibilityPlanning.value.bicycleCount !== undefined &&
      visibilityPlanningCapacityDto.value?.usedBicyclesOnLocation !== null &&
      visibilityPlanningCapacityDto.value?.usedBicyclesOnLocation !== undefined &&
      (visibilityPlanningCapacityDto.value.usedBicyclesOnLocation
        + visibilityPlanning.value.bicycleCount) > visibilityLocationOption.value.quota) {
      const answer = await confirm?.(t('pages.visibility.planning.modal.confirmQuotaExceeded'));
      if (!answer) {
        return;
      }
    }

    let data;
    if (isViewMode.value) {
      data = {
        campaign: visibilityPlanning.value.campaign,
        newDesign: visibilityPlanning.value.newDesign,
        comment: visibilityPlanning.value.comment,
        responsibleUserForTransfer: visibilityPlanning.value.responsibleUserForTransfer,
      };
    } else if (isConfirmationMode.value) {
      data = {
        campaign: visibilityPlanning.value.campaign,
        visibilityBicycles: visibilityPlanning.value.visibilityBicycles?.map((bike) => (bike as EnrichedVisibilityBicycleOption).value) || [],
        newDesign: visibilityPlanning.value.newDesign,
        comment: visibilityPlanning.value.comment,
        responsibleUserForTransfer: visibilityPlanning.value.responsibleUserForTransfer,
      };
    } else {
      data = {
        region: visibilityPlanning.value.region,
        campaign: visibilityPlanning.value.campaign,
        location: visibilityPlanning.value.location,
        visibilityBicycles: visibilityPlanning.value.visibilityBicycles?.map((bike) => (bike as EnrichedVisibilityBicycleOption).value) || [],
        bicycleCount: visibilityPlanning.value.bicycleCount,
        startTime: visibilityPlanning.value.time?.start,
        endTime: visibilityPlanning.value.time?.end,
        newDesign: visibilityPlanning.value.newDesign,
        comment: visibilityPlanning.value.comment,
        responsibleUserForTransfer: visibilityPlanning.value.responsibleUserForTransfer,
      };
    }

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: data,
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');

    // do not close modal if chaining is active
    if (!chaining.value) {
      closeModal();
    } else {
      // get capacity again.
      await refetchVisibilityPlanningCapacityDto();
      chainingCtr.value += 1;
    }
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
